import { Capacitor } from "@capacitor/core";
import type { Platform } from "@highmarch/app";

/**
 * Which shell the page runs in (FINISH_PLAN WP4.3): the browser (the tunnel play-test, the home-screen PWA) or the
 * Capacitor iOS app. Only Infrastructure asks; Presentation reads the `Platform` flags (CLAUDE.md, "Infrastructure
 * behind interfaces").
 */
export function isNativeShell(): boolean {
  try { return Capacitor.isNativePlatform(); } catch { return false; }
}

/**
 * The page runs as an installed web app (Add to Home Screen; any standalone display mode), not in a browser tab.
 * Only then does the offline cache fetch the whole game in the background (main.ts): a Safari tab on mobile data must
 * not pull several hundred MB the player never asked for, and its storage is wiped after 7 days without a visit anyway.
 */
export function isInstalledWebApp(): boolean {
  try {
    if ((navigator as Navigator & { standalone?: boolean }).standalone === true) return true;
    return typeof matchMedia === "function" && matchMedia("(display-mode: standalone), (display-mode: fullscreen)").matches;
  } catch { return false; }
}

/**
 * The iOS shell's capabilities. `supportsIap` means real money through StoreKit 2; the web build's sandbox store is
 * not IAP (Store.kind tells the two apart). Push stays off: there is no server to send one (ADR 0012).
 */
export const iosPlatform: Platform = { name: "ios", supportsPush: false, supportsIap: true, supportsLocalNotifications: true, supportsHaptics: true };

/**
 * The browser's: haptics where the web can give them (the Vibration API on Android; the switch control's tick on iOS
 * Safari 18, haptics.ts); no local notifications without a server (a web push needs one).
 */
export function browserPlatform(): Platform {
  return { name: "web", supportsPush: false, supportsIap: false, supportsLocalNotifications: false, supportsHaptics: webHapticsAvailable() };
}

/** The Vibration API, or an `<input type=checkbox switch>` (iOS Safari 17.4+, which ticks the Taptic Engine from 18). */
export function webHapticsAvailable(): boolean {
  try {
    if (typeof navigator !== "undefined" && typeof navigator.vibrate === "function") return true;
    if (typeof document === "undefined") return false;
    const probe = document.createElement("input");
    return "switch" in probe;
  } catch { return false; }
}

let cached: Platform | null = null;
/** This shell's Platform, chosen once. */
export function currentPlatform(): Platform {
  return (cached ??= isNativeShell() ? iosPlatform : browserPlatform());
}
